/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The client's roles, `demand_profile.client_roles` (plan 2026-10-28, §5.2).
 *
 * Mirrors `housien/models/client/client_role.py::CLIENT_ROLE_VALUES`, in the
 * same display order.
 */
export const CLIENT_ROLE_VALUES = [
  'seeker',
  'owner',
  'sales_partner',
  'exchange',
  'developer',
  'investor',
  'short_term',
] as const;

export type ClientRoleValue = (typeof CLIENT_ROLE_VALUES)[number];

/**
 * Reads the legacy single `client_role` as a list (`both` is two roles).
 *
 * @param legacy - The stored `client_role`, if any.
 * @returns The roles it stands for.
 */
function rolesFromLegacy(legacy: unknown): string[] {
  if (typeof legacy !== 'string' || !legacy) return [];
  return legacy === 'both' ? ['seeker', 'owner'] : [legacy];
}

/**
 * The client's effective roles, whichever form the record stores them in.
 *
 * `client_roles` wins whenever it holds a list, an empty list included (a
 * deliberate "no roles"); otherwise the legacy `client_role` is read as a
 * list. Same rule as the backend's `effective_client_roles`.
 *
 * @param demandProfile - The stored `demand_profile` bag.
 * @returns The roles, in stored order.
 */
export function effectiveClientRoles(demandProfile: Record<string, unknown> | undefined): string[] {
  const listed = demandProfile?.client_roles;
  if (Array.isArray(listed)) return listed.map(String);
  return rolesFromLegacy(demandProfile?.client_role);
}

/**
 * The i18n key (namespace `features/clients`) for one role's label.
 *
 * @param role - A role value.
 * @returns The key under `roles.*`.
 */
export function clientRoleLabelKey(role: string): string {
  return `roles.${role}`;
}
